import React, { useMemo } from 'react'
import { IconButton, Toggle, Whisper, Tooltip } from 'rsuite'
import { Cell } from 'rsuite-table'
import EyeIcon from '@rsuite/icons/legacy/Eye'
import EditIcon from '@rsuite/icons/legacy/Edit'
import TrashIcon from '@rsuite/icons/legacy/Trash'
import DownloadIcon from '@rsuite/icons/legacy/Download'
import ClockIcon from '@rsuite/icons/legacy/ClockO'
import UnlockIcon from '@rsuite/icons/legacy/Unlock'
import CalendarPlusIcon from '@rsuite/icons/legacy/CalendarPlusO'
import SignInIcon from '@rsuite/icons/legacy/SignIn'
import SettingIcon from '@rsuite/icons/legacy/Setting'
import ShieldIcon from '@rsuite/icons/legacy/Shield'
import PlusIcon from '@rsuite/icons/legacy/Plus'
import ShareIcon from '@rsuite/icons/legacy/Share'
import UnlinkIcon from '@rsuite/icons/legacy/Unlink'
import './actionCell.scss'

type Props = {
  rowData?: any
  dataKey: string
  onAction?: (eventKey: string | undefined, rowData: any) => void
  actionOptions?: string[] | ((rowData: any) => string[])
  disabledActions?: string[] | ((rowData: any) => string[])
  isDeleteHideOn?: string
  toggleKey?: string
  toggleDisabled?: boolean | ((rowData: any) => boolean)
}

const defaultProps = {
  rowData: undefined,
  actionOptions: [],
  onAction: () => {}
}

const ACTION_CONFIG: Record<
  string,
  { eventKey: string; icon: React.ReactElement; tooltip: string }
> = {
  View: { eventKey: '1', icon: <EyeIcon />, tooltip: 'View' },
  Preview: { eventKey: '12', icon: <EyeIcon />, tooltip: 'Preview' },
  Edit: { eventKey: '2', icon: <EditIcon />, tooltip: 'Edit' },
  Download: { eventKey: '5', icon: <DownloadIcon />, tooltip: 'Download' },
  History: { eventKey: '6', icon: <ClockIcon />, tooltip: 'History' },
  Delete: { eventKey: '4', icon: <TrashIcon />, tooltip: 'Delete' },
  Lift: { eventKey: '7', icon: <UnlockIcon />, tooltip: 'Lift block' },
  Extend: { eventKey: '8', icon: <CalendarPlusIcon />, tooltip: 'Extend block' },
  LoginAttempts: { eventKey: '9', icon: <SignInIcon />, tooltip: 'Login attempts' },
  // eventKey '13' (not '3') so this can coexist with the Toggle in one cell —
  // the Toggle also fires '3'.
  'Manage permissions': { eventKey: '13', icon: <SettingIcon />, tooltip: 'Manage permissions' },
  'Manage MFA': { eventKey: '14', icon: <ShieldIcon />, tooltip: 'Manage two-factor authentication' },
  Assign: { eventKey: '10', icon: <PlusIcon />, tooltip: 'Assign project' },
  Share: { eventKey: '11', icon: <ShareIcon />, tooltip: 'Copy share link' },
  // One action for both directions — the caller reads the row's `revoked`
  // field to decide which way to write it.
  Revoke: { eventKey: '15', icon: <UnlinkIcon />, tooltip: 'Project access' }
}

const ActionCell = ({
  rowData,
  dataKey,
  actionOptions,
  disabledActions,
  isDeleteHideOn,
  onAction,
  toggleKey,
  toggleDisabled,
  ...props
}: Props) => {
  const resolvedActionOptions = useMemo(
    () =>
      typeof actionOptions === 'function'
        ? actionOptions(rowData)
        : actionOptions,
    [actionOptions, rowData]
  )

  const resolvedDisabledActions = useMemo(
    () =>
      typeof disabledActions === 'function'
        ? disabledActions(rowData)
        : disabledActions ?? [],
    [disabledActions, rowData]
  )

  const filteredOptions = useMemo(() => {
    const options = resolvedActionOptions?.length
      ? resolvedActionOptions.filter((opt) => opt !== 'Toggle')
      : Object.keys(ACTION_CONFIG)

    if (rowData && isDeleteHideOn && rowData[isDeleteHideOn] === false) {
      return options.filter((opt) => opt !== 'Delete')
    }

    return options
  }, [resolvedActionOptions, rowData, isDeleteHideOn])

  const showToggle = resolvedActionOptions?.includes('Toggle')

  const handleToggle = () => {
    if (typeof onAction === 'function') {
      onAction('3', rowData)
    }
  }

  return (
    <Cell {...props} className="action-cell">
      <div
        className="action-cell__buttons"
        onClick={(e) => e.stopPropagation()}
      >
        {filteredOptions.map((option) => {
          const config = ACTION_CONFIG[option]
          if (!config) return null
          const isDisabled = resolvedDisabledActions.includes(option)

          return (
            <Whisper
              key={option}
              placement="bottom"
              trigger="hover"
              speaker={<Tooltip>{config.tooltip}</Tooltip>}
              // Portal the tooltip to document.body so it escapes the
              // rsuite Table cell's overflow:hidden clipping. Without this
              // the tooltip renders inside the cell and is clipped away,
              // making it look like tooltips aren't working at all.
              container={() => document.body}
              delayOpen={150}
            >
              {/*
                Wrap in a span so the hover trigger still fires when the
                IconButton is `disabled` — disabled <button> elements
                swallow pointer events in most browsers, which means the
                Whisper hover handler never runs on disabled actions.
              */}
              <span
                style={{ display: 'inline-flex' }}
                onClick={(e) => e.stopPropagation()}
              >
                <IconButton
                  className="action-cell__btn"
                  appearance="subtle"
                  size="sm"
                  icon={config.icon}
                  aria-label={config.tooltip}
                  disabled={isDisabled}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (isDisabled) return
                    if (typeof onAction === 'function') {
                      onAction(config.eventKey, rowData)
                    }
                  }}
                />
              </span>
            </Whisper>
          )
        })}
        {showToggle && (
          <Toggle
            className="action-cell__toggle"
            size="md"
            checked={toggleKey ? !!rowData?.[toggleKey] : !!rowData?.isActive}
            disabled={
              typeof toggleDisabled === 'function'
                ? toggleDisabled(rowData)
                : !!toggleDisabled
            }
            onChange={handleToggle}
          />
        )}
      </div>
    </Cell>
  )
}

ActionCell.defaultProps = defaultProps

export default ActionCell
